import React, { useEffect, useState, useCallback } from 'react';
import {
	Box,
	Flex,
	Button,
	Text,
	Spacer,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Spinner,
} from '@chakra-ui/react';
import AddSampleModal from './AddSampleModal';
import Locale from '../../../utils/translations';
import { BonfireAPI, ApiType } from '../../../models/BonfireApi';
import config from '../../../utils/config';
import { Sample } from '../../../utils/interfaces';
import SampleDetailsModal from './SampleDetailsModal';

const SamplesPage: React.FC = () => {
	const [samples, setSamples] = useState<Sample[]>([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [addSampleError, setAddSampleError] = useState('');
	const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
	const [isDetailsOpen, setIsDetailsOpen] = useState(false);

	const fetchSamples = useCallback(async () => {
		setIsLoading(true);
		const url = `${config.qa_api.url}/aiModelComparisonsSamples/list`;
		const api = new BonfireAPI(ApiType.QaApi);
		try {
			const response = await api.get(url);
			setSamples(response);
		} catch (err) {
			console.error('Failed to fetch samples', err);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchSamples();
	}, [fetchSamples]);

	const handleAddSample = useCallback(
		async (sampleName: string, audioFile?: File, gtFile?: File, languages?: string[]) => {
			const url = `${config.qa_api.url}/aiModelComparisonsSamples/add`;
			const api = new BonfireAPI(ApiType.QaApi);

			const formData = new FormData();
			formData.append('name', sampleName);
			formData.append('type', 't2s');
			formData.append('languages', languages ? languages.join(',') : '');

			if (gtFile) {
				formData.append('groundtruth', gtFile);
			}
			if (audioFile) {
				formData.append('audio', audioFile);
			}

			setIsCreating(true);
			setAddSampleError('');
			try {
				const response = await api.postWithConfig(url, formData, {
					headers: { 'Content-Type': 'multipart/form-data' },
				});
				if (response?.response?.status === 400) {
					const errorMsg = response?.response.data.error || 'Failed to add sample';
					setAddSampleError(errorMsg);
					return;
				}

				await fetchSamples();
				setIsModalOpen(false);
				setAddSampleError('');
			} catch (err) {
				setAddSampleError('Failed to add sample');
				console.error('Failed to add sample', err);
			} finally {
				setIsCreating(false);
			}
		},
		[fetchSamples],
	);
	const handleDeleteSample = async (sampleId: string) => {
		const url = `${config.qa_api.url}/aiModelComparisonsSamples/delete/${sampleId}`;
		const api = new BonfireAPI(ApiType.QaApi);
		try {
			await api.delete(url, {});
			await fetchSamples();
		} catch (err) {
			console.error('Failed to delete sample', err);
		}
	};

	return (
		<Box p={6}>
			<Flex mb={6} alignItems="center">
				<Text fontSize="2xl" fontWeight="bold">
					{Locale.get('Samples')}
				</Text>
				<Spacer />
				<Button variant="bePrimary" onClick={() => setIsModalOpen(true)}>
					{Locale.get('Add sample')}
				</Button>
			</Flex>

			{isLoading ? (
				<Spinner />
			) : (
				<Table variant="simple" bg="white" borderRadius="md" boxShadow="sm">
					<Thead>
						<Tr>
							<Th>Name</Th>
							<Th>Type</Th>
							<Th>Languages</Th>
						</Tr>
					</Thead>
					<Tbody>
						{samples.map(sample => (
							<Tr key={sample.sampleId}>
								<Td fontWeight="bold">{sample.name}</Td>
								<Td>{sample.type}</Td>
								<Td>{sample.languages}</Td>
								<Td display={'flex'} justifyContent={'flex-end'} align="center">
									<Button
										size="sm"
										colorScheme="blue"
										variant="outline"
										onClick={() => {
											setSelectedSample(sample);
											setIsDetailsOpen(true);
										}}
										mr={2}
									>
										See Details
									</Button>
									<Button
										size="sm"
										colorScheme="red"
										variant="beDanger"
										onClick={() => handleDeleteSample(sample.sampleId)}
									>
										Delete
									</Button>
								</Td>
							</Tr>
						))}
					</Tbody>
				</Table>
			)}
			<SampleDetailsModal
				isOpen={isDetailsOpen}
				onClose={() => setIsDetailsOpen(false)}
				sample={selectedSample}
			/>
			<AddSampleModal
				isOpen={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					setAddSampleError('');
				}}
				onAddSample={handleAddSample}
				isCreating={isCreating}
				error={addSampleError}
				setError={setAddSampleError}
			/>
		</Box>
	);
};

export default SamplesPage;
